import { useCallback, useMemo } from "react";

import { useGetTranscript } from "../api/use-get-transcript";
import { useGetTranscriptMetadata } from "../api/use-get-transcript-metadata";
import {
  APITranscript,
  AsyncStatus,
  TranscriptFormat,
  TranscriptMetadata,
} from "../types/types";
import { downloadFile } from "../utils/download-file";

async function readFile(
  blob: Blob,
  readAs: "text" | "dataUrl",
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target) resolve(event.target.result as string);
    };
    reader.onerror = reject;
    switch (readAs) {
      case "text":
        reader.readAsText(blob);
        break;
      case "dataUrl":
        reader.readAsDataURL(blob);
        break;
      default:
        throw new Error(`Invalid readAs: ${readAs}`);
    }
  });
}

type DownloadTranscriptFunction = (
  transcriptId: string,
  downloadFormat: TranscriptFormat,
  options?: {
    transcript?: APITranscript;
    transcriptMeta?: TranscriptMetadata;
  },
) => Promise<void>;

export function useDownloadTranscript(): [
  status: AsyncStatus,
  downloadTranscript: DownloadTranscriptFunction,
] {
  const [status, getTranscript] = useGetTranscript(false);
  const getTranscriptMetadata = useGetTranscriptMetadata();

  const downloadTranscript = useCallback<DownloadTranscriptFunction>(
    async (
      transcriptId: string,
      downloadFormat: TranscriptFormat,
      options?: {
        transcriptMeta?: TranscriptMetadata;
      },
    ) => {
      const { transcriptMeta } = options || {};

      let downloadFilename = transcriptId;
      try {
        downloadFilename = (
          transcriptMeta || (await getTranscriptMetadata(transcriptId))
        ).name;
      } catch {
        // noop, fall back to id
      }

      switch (downloadFormat) {
        case "application/json": {
          const blob = (await getTranscript({
            transcriptId,
            accept: downloadFormat,
            forceBlob: true,
          })) as Blob;
          const file = await readFile(blob, "text");
          downloadFile(`${downloadFilename}.json`, file);
          break;
        }
        case "text/plain": {
          const blob = (await getTranscript({
            transcriptId,
            accept: downloadFormat,
          })) as Blob;
          const file = await readFile(blob, "text");
          downloadFile(`${downloadFilename}.txt`, file);
          break;
        }
        case "text/vtt": {
          const blob = (await getTranscript({
            transcriptId,
            accept: downloadFormat,
          })) as Blob;
          const file = await readFile(blob, "text");
          downloadFile(`${downloadFilename}.vtt`, file);
          break;
        }
        case "application/vnd.openxmlformats-officedocument.wordprocessingml.document": {
          const blob = (await getTranscript({
            transcriptId,
            accept: downloadFormat,
          })) as Blob;
          const file = await readFile(blob, "dataUrl");
          downloadFile(`${downloadFilename}.docx`, file, false);
          break;
        }
        default:
          throw new Error(`Invalid download format: ${downloadFormat}`);
      }
    },
    [getTranscript, getTranscriptMetadata],
  );

  return useMemo(
    () => [status, downloadTranscript],
    [status, downloadTranscript],
  );
}
